import { ScanResult } from "./types";

/**
 * The existing dataset scan results are being added to, reduced to what decides whether a
 * scan can join it. The backend's select_folders refuses folders whose Job Name, Setup
 * Name or Recipes Name differ from the dataset's `camtek_db_export`, so these are the
 * fields that matter.
 */
export interface TargetDataset {
  id: string;
  name: string;
  job?: string;
  setup?: string;
  recipes?: string[];
}

interface DatasetLike {
  dataset_id: string;
  display_name: string;
  metadata?: { camtek_db_export?: Record<string, unknown> } | null;
}

export function toTargetDataset(dataset: DatasetLike): TargetDataset {
  const meta = dataset.metadata?.camtek_db_export ?? {};
  const job = meta["Job Name"];
  const setup = meta["Setup Name"];
  const recipes = meta["Recipes Name"];
  return {
    id: dataset.dataset_id,
    name: dataset.display_name,
    ...(typeof job === "string" && job && { job }),
    ...(typeof setup === "string" && setup && { setup }),
    ...(Array.isArray(recipes) && recipes.length && { recipes }),
  };
}

/**
 * Why a scan result cannot be added to the target dataset, or null if nothing rules it
 * out. Only sure mismatches count: unreadable recipes, or recipes listed in another
 * order, are left for the backend to decide rather than greyed out on a guess.
 */
export function incompatibilityReason(
  result: ScanResult,
  target: TargetDataset,
): string | null {
  if (target.job && result.job !== target.job) {
    return `Job differs from the dataset's (${target.job})`;
  }
  if (target.setup && result.setup !== target.setup) {
    return `Setup differs from the dataset's (${target.setup})`;
  }
  if (target.recipes && result.recipe) {
    // DataServer joins recipe names with ", " (scan_results_client.fetch_recipes).
    const scanRecipes = new Set(result.recipe.split(", "));
    const same =
      scanRecipes.size === new Set(target.recipes).size &&
      target.recipes.every((name) => scanRecipes.has(name));
    if (!same) {
      return `Recipes differ from the dataset's (${target.recipes.join(", ")})`;
    }
  }
  return null;
}
